// src/components/Admin/BalanceAdmin.js
import React, { useState, useEffect, useCallback } from 'react';

export default function BalanceAdmin({ token }) {
  const [humanScore, setHumanScore] = useState(3);
  const [reptileScore, setReptileScore] = useState(7);
  const [totalPoints, setTotalPoints] = useState(10);
  const [lore, setLore] = useState([]);
  const [newLoreLine, setNewLoreLine] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // ── Load current balance ────────────────────────────────────────────────────
  const fetchBalance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/balance');
      if (res.ok) {
        const data = await res.json();
        setHumanScore(data.humanScore ?? 3);
        setReptileScore(data.reptileScore ?? 7);
        setTotalPoints(data.totalPoints ?? 10);
        setLore(data.lore ?? []);
      }
    } catch (err) {
      console.error('[BALANCE ADMIN] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  // ── Score handlers (always sum to totalPoints) ──────────────────────────────
  const handleReptileChange = (val) => {
    const r = Math.max(0, Math.min(totalPoints, parseInt(val) || 0));
    setReptileScore(r);
    setHumanScore(totalPoints - r);
  };

  const handleHumanChange = (val) => {
    const h = Math.max(0, Math.min(totalPoints, parseInt(val) || 0));
    setHumanScore(h);
    setReptileScore(totalPoints - h);
  };

  const handleTotalChange = (val) => {
    const t = Math.max(1, parseInt(val) || 10);
    setTotalPoints(t);
    const r = Math.min(reptileScore, t);
    setReptileScore(r);
    setHumanScore(t - r);
  };

  // ── Lore management ────────────────────────────────────────────────────────
  const addLore = () => {
    if (!newLoreLine.trim()) return;
    setLore(prev => [...prev, newLoreLine.trim()]);
    setNewLoreLine('');
  };

  const removeLore = (i) => {
    setLore(prev => prev.filter((_, idx) => idx !== i));
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const saveBalance = async () => {
    setSaving(true);
    setMessage('');

    try {
      const res = await fetch('/api/balance', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          humanScore,
          reptileScore,
          totalPoints,
          lore,
        }),
      });

      if (res.ok) {
        setMessage(`✓ Balance updated — Humans ${humanScore} : Reptiles ${reptileScore}`);
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage(`✗ ${err.error || `Failed (${res.status})`}`);
      }
    } catch (err) {
      setMessage('✗ Connection error');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  if (loading) {
    return <div style={{ color: '#555', padding: '20px', textAlign: 'center' }}>Loading balance...</div>;
  }

  return (
    <div>
      {/* Live Preview */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid #222',
        background: '#080808',
        alignItems: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#4a9eff', fontSize: '0.75rem', letterSpacing: '2px' }}>HUMANS</div>
          <div style={{ color: '#4a9eff', fontSize: '2.4rem', fontWeight: 'bold', textShadow: '0 0 10px #4a9eff44' }}>
            {humanScore}
          </div>
        </div>
        <div style={{ color: '#333', fontSize: '1.5rem' }}>—</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#00ff41', fontSize: '0.75rem', letterSpacing: '2px' }}>REPTILES</div>
          <div style={{ color: '#00ff41', fontSize: '2.4rem', fontWeight: 'bold', textShadow: '0 0 10px #00ff4144' }}>
            {reptileScore}
          </div>
        </div>
      </div>

      {/* Slider */}
      <div style={{ marginBottom: '20px', padding: '0 10px' }}>
        <label style={{ color: '#666', fontSize: '0.75rem', display: 'block', marginBottom: '8px' }}>
          DRAG TO SHIFT THE BALANCE:
        </label>
        <input
          type="range"
          min={0}
          max={totalPoints}
          value={reptileScore}
          onChange={(e) => handleReptileChange(e.target.value)}
          style={{ width: '100%', cursor: 'pointer', accentColor: '#ffaa00' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginTop: '4px' }}>
          <span style={{ color: '#4a9eff' }}>← HUMAN DOMINANCE</span>
          <span style={{ color: '#00ff41' }}>REPTILE DOMINANCE →</span>
        </div>
      </div>

      {/* Number Inputs */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Humans', value: humanScore, onChange: handleHumanChange, color: '#4a9eff' },
          { label: 'Reptiles', value: reptileScore, onChange: handleReptileChange, color: '#00ff41' },
          { label: 'Total Pts', value: totalPoints, onChange: handleTotalChange, color: '#ffaa00' },
        ].map(({ label, value, onChange, color }) => (
          <div key={label}>
            <label style={{ color, fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>
              {label.toUpperCase()}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              min={0}
              max={100}
              style={{
                background: '#0a0a0a',
                border: `1px solid ${color}44`,
                color,
                padding: '8px 12px',
                fontFamily: "'VT323', 'Courier New', monospace",
                fontSize: '1.2rem',
                width: '80px',
                textAlign: 'center',
              }}
            />
          </div>
        ))}
      </div>

      {/* Lore / Intel Messages */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#666', fontSize: '0.8rem', display: 'block', marginBottom: '8px' }}>
          INTEL TICKER MESSAGES (shown to visitors):
        </label>

        {lore.length === 0 && (
          <div style={{ color: '#333', fontSize: '0.8rem', padding: '8px 0' }}>
            No custom messages. Default messages will be shown.
          </div>
        )}

        {lore.map((line, i) => (
          <div key={i} style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '4px',
            padding: '5px 8px',
            background: '#0a0a0a',
            border: '1px solid #151515',
          }}>
            <span style={{ color: '#555', fontSize: '0.8rem', flex: 1 }}>{line}</span>
            <button
              onClick={() => removeLore(i)}
              style={{
                background: 'none',
                border: '1px solid #ff004044',
                color: '#ff0040',
                cursor: 'pointer',
                padding: '2px 8px',
                fontFamily: "'VT323', monospace",
                fontSize: '0.75rem',
              }}
            >
              ✗
            </button>
          </div>
        ))}

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <input
            type="text"
            value={newLoreLine}
            onChange={(e) => setNewLoreLine(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addLore()}
            placeholder="Add intel message..."
            style={{
              flex: 1,
              background: '#0a0a0a',
              border: '1px solid #222',
              color: '#aaa',
              padding: '6px 10px',
              fontFamily: "'VT323', monospace",
              fontSize: '0.85rem',
            }}
          />
          <button
            onClick={addLore}
            disabled={!newLoreLine.trim()}
            className="bbs-button"
            style={{
              padding: '6px 14px',
              fontSize: '0.8rem',
              opacity: newLoreLine.trim() ? 1 : 0.3,
            }}
          >
            ADD
          </button>
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          className="bbs-button"
          onClick={saveBalance}
          disabled={saving}
          style={{
            padding: '10px 24px',
            fontSize: '0.9rem',
            borderColor: '#00ff41',
            color: '#00ff41',
            opacity: saving ? 0.5 : 1,
          }}
        >
          {saving ? 'SAVING...' : 'SAVE BALANCE'}
        </button>
        <button
          className="bbs-button"
          onClick={fetchBalance}
          style={{ padding: '10px 16px', fontSize: '0.8rem', borderColor: '#444', color: '#666' }}
        >
          RELOAD
        </button>
      </div>

      {/* Status */}
      {message && (
        <div style={{
          marginTop: '12px',
          padding: '8px 12px',
          fontSize: '0.85rem',
          color: message.startsWith('✓') ? '#00ff41' : '#ff0040',
          border: '1px solid #1a1a1a',
          background: '#0a0a0a',
        }}>
          {message}
        </div>
      )}
    </div>
  );
}